'use client'

import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Link from 'next/link'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useDebouncedCallback } from 'use-debounce'
import { useRegion } from '@/shared/hooks/use-region'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { buildRegionUrl } from '@/shared/lib/utils'
import { useStmGroupsForTab } from '@/features/stm/hooks/use-stm-weeks'
import {
  useStmDeleteGroup,
  useStmUpdateGroup,
} from '@/features/stm/hooks/use-stm-groups-blocks'
import { GroupFormDialog } from '@/features/stm/components/forms/GroupFormDialog'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Input } from '@/shared/components/ui/input'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Plus, Search, Edit2, Trash2, Users, UserCheck, FileCheck, Filter, X, CheckSquare, ArrowUpDown, Phone, Hash, Banknote, ShieldCheck, CreditCard, FileText, ChevronDown, Check, UtensilsCrossed, RefreshCw } from 'lucide-react'
import { format } from 'date-fns'
import { formatGroupDateRange } from '@/features/stm/lib/date-utils'
import { cn } from '@/shared/lib/utils'
import { toast } from 'sonner'
import type { Group, TripType } from '@/features/stm/lib/types'
import { UserRole } from '@/shared/constants/user-roles'
import { useSession } from '@/features/auth/context/session.context'
import {
  SEARCH_PARAMS_RETURN_GROUP_ID_KEY,
  SEARCH_PARAMS_TAB_KEY,
  VOLUNTEER_TAB_FILTER_SEARCH,
} from '@/features/volunteers/constants/volunteer-tab-links'

type SortField = 'start_date' | 'group_name' | 'status' | 'participants' | 'created_at'
type SortDirection = 'asc' | 'desc'

const tripTypeStyles: Record<TripType, string> = {
  'Vision Trip': 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500',
  'Skilled Labor': 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500',
  'Volunteer': 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500',
  'Youth Trip': 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500',
  'Adult Trip': 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 border-indigo-500',
  'Family Trip': 'bg-pink-500/15 text-pink-700 dark:text-pink-400 border-pink-500',
  'Semesters': 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 border-cyan-500',
  'Gap': 'bg-teal-500/15 text-teal-700 dark:text-teal-400 border-teal-500',
  'WR': 'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500',
  'Leadership': 'bg-slate-500/15 text-slate-700 dark:text-slate-400 border-slate-500',
  'Partner': 'bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500',
  'Other': 'bg-gray-500/15 text-gray-700 dark:text-gray-400 border-gray-500',
}

const sortOptions: { value: SortField; label: string }[] = [
  { value: 'start_date', label: 'Start Date' },
  { value: 'group_name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'participants', label: 'Participants' },
  { value: 'created_at', label: 'Date Added' },
]

type FilterKey =
  | 'leader_assigned'
  | 'pretrip_info_given'
  | 'deposit_received'
  | 'background_check_complete'
  | 'full_payment_received'
  | 'post_trip_info'

const filterOptions: { value: FilterKey; label: string }[] = [
  { value: 'leader_assigned', label: 'Leader Assigned' },
  { value: 'pretrip_info_given', label: 'Pre-Trip Info' },
  { value: 'deposit_received', label: 'Deposit Received' },
  { value: 'background_check_complete', label: 'Background Check' },
  { value: 'full_payment_received', label: 'Full Payment' },
  { value: 'post_trip_info', label: 'Post Trip Info' },
]

type RevenueCategory = 'STM Revenue' | 'AR Revenue' | 'No Revenue'

function categorizeProjectRevenue(projectId: string | null | undefined): RevenueCategory {
  const normalizedText = (projectId ?? '').trim().toLowerCase()
  if (normalizedText === '' || normalizedText === 'na' || normalizedText === 'none' || normalizedText === 'no') {
    return 'No Revenue'
  }

  const normalized = (projectId ?? '').trim().toUpperCase()
  if (normalized === 'ARVOL' || normalized === 'AR-VOL') {
    return 'No Revenue'
  }
  if (normalized.includes('AR') || normalized.includes('D') || normalized.includes('P') || normalized.includes('R')) {
    return 'AR Revenue'
  }
  if (normalized.includes('Y') || normalized.includes('E') || normalized.includes('F')) {
    return 'STM Revenue'
  }
  return 'No Revenue'
}

function isDietaryRestrictionsChecked(group: Group): boolean {
  if (group.dietary_restrictions) return true
  if (group.has_member_dietary_data) return true
  return (group.dietary_restrictions_details ?? '').trim().length > 0
}

/** When set, only groups where this user is contact or mission leader are shown (Group/Mission Leader view) */
export type RestrictToLeader = { email: string }

function filterGroupsToLeader(groups: Group[], restrictToLeader?: RestrictToLeader | null): Group[] {
  if (!restrictToLeader?.email?.trim()) return groups
  const email = restrictToLeader.email.trim().toLowerCase()
  return groups.filter(
    (g) =>
      (g.contact_email?.trim().toLowerCase() === email) ||
      (g.leader_email?.trim().toLowerCase() === email)
  )
}

interface StmGroupsTabProps {
  regionId?: number | undefined | null
  /** Optional calendar context used to preserve week/month when returning from group detail */
  weekStartContext?: string
  /** Optional group id to restore viewport position when returning from group detail */
  returnToGroupId?: string
  /** When set, only groups where this user is contact or mission leader are shown (Group/Mission Leader view) */
  restrictToLeader?: RestrictToLeader
  /** When true, user is a group contact (Volunteer/TeamLeader/Partner) viewing only their groups - hide Add, Delete, bulk actions */
  isGroupContactView?: boolean
  /** Current user id for contact-only edit controls in group-contact view */
  currentUserId?: number
  /** Current user role for contact-view action restrictions */
  currentUserRole?: string
}

export function StmGroupsTab({
  regionId,
  weekStartContext,
  returnToGroupId,
  restrictToLeader,
  isGroupContactView,
  currentUserId,
  currentUserRole,
}: StmGroupsTabProps) {
  const queryClient = useQueryClient()
  const { session } = useSession()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { regionSlug } = useRegion()
  const { isLoading: regionIdResolving } = useRegionId()
  const { data: rawGroups, isLoading: groupsQueryLoading } = useStmGroupsForTab(
    regionId ?? null,
    weekStartContext
  )
  const groupsLoading = regionIdResolving || groupsQueryLoading
  const hasRestoredScrollRef = useRef(false)
  const groups = useMemo(
    () => filterGroupsToLeader(rawGroups ?? [], restrictToLeader),
    [rawGroups, restrictToLeader]
  )
  const getGroupContextParams = (extra?: Record<string, string>) => ({
    from: 'groups',
    ...(weekStartContext ? { weekStart: weekStartContext } : {}),
    ...(returnToGroupId ? { returnToGroupId } : {}),
    ...(extra ?? {}),
  })
  const groupDetailHref = (groupId: string) =>
    regionSlug
      ? buildRegionUrl(
          regionSlug,
          `/volunteers/groups/${groupId}`,
          getGroupContextParams({ returnToGroupId: groupId })
        )
      : `/volunteers/groups/${groupId}?${new URLSearchParams(getGroupContextParams({ returnToGroupId: groupId })).toString()}`

  /** Link to group detail with Volunteers tab active */
  const groupVolunteersHref = (groupId: string) =>
    regionSlug
      ? buildRegionUrl(
          regionSlug,
          `/volunteers/groups/${groupId}`,
          getGroupContextParams({ tab: 'members', returnToGroupId: groupId })
        )
      : `/volunteers/groups/${groupId}?${new URLSearchParams(getGroupContextParams({ tab: 'members', returnToGroupId: groupId })).toString()}`
  const deleteGroup = useStmDeleteGroup()
  const updateGroup = useStmUpdateGroup()
  const [formOpen, setFormOpen] = useState(false)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(new Set())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [sortField, setSortField] = useState<SortField>('start_date')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  useEffect(() => {
    setSearch(searchParams.get('search') || '')
  }, [searchParams])

  const updateSearchParam = useDebouncedCallback((value: string) => {
    const params = new URLSearchParams(searchParams.toString())
    if (value && value.trim().length > 0) {
      params.set('search', value)
    } else {
      params.delete('search')
    }
    params.set('page', '1')
    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false })
  }, 750)

  const handleOpenVolunteerSearch = () => {
    const trimmedSearch = search.trim()
    if (!trimmedSearch) return
    updateSearchParam.cancel()
    const params = {
      [SEARCH_PARAMS_TAB_KEY]: VOLUNTEER_TAB_FILTER_SEARCH,
      page: '1',
      search: trimmedSearch,
    }
    const href = regionSlug
      ? buildRegionUrl(regionSlug, '/volunteers', params)
      : `/volunteers?${new URLSearchParams(params).toString()}`
    router.push(href)
  }

  const clearSearchParam = () => {
    updateSearchParam.cancel()
    setSearch('')
    const params = new URLSearchParams(searchParams.toString())
    params.delete('search')
    params.set('page', '1')
    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false })
  }

  const clearGroupFilters = () => {
    clearSearchParam()
    setActiveFilters(new Set())
  }

  const handleToggleLeader = (g: Group) => {
    updateGroup.mutate({ id: g.id, leader_assigned: !g.leader_assigned })
  }

  const handleTogglePretrip = (g: Group) => {
    updateGroup.mutate({ id: g.id, pretrip_info_given: !g.pretrip_info_given })
  }

  const handleToggleDeposit = (g: Group) => {
    updateGroup.mutate({ id: g.id, deposit_received: !(g.deposit_received ?? false) })
  }

  const handleToggleBackgroundCheck = (g: Group) => {
    updateGroup.mutate({ id: g.id, background_check_complete: !(g.background_check_complete ?? false) })
  }

  const handleToggleFullPayment = (g: Group) => {
    updateGroup.mutate({ id: g.id, full_payment_received: !(g.full_payment_received ?? false) })
  }

  const handleTogglePostTrip = (g: Group) => {
    updateGroup.mutate({ id: g.id, post_trip_info: !(g.post_trip_info ?? false) })
  }

  const handleToggleDietaryRestrictions = (g: Group) => {
    updateGroup.mutate({ id: g.id, dietary_restrictions: !isDietaryRestrictionsChecked(g) })
  }

  const toggleSelection = (id: string) => {
    setSelectedIds((p) => {
      const n = new Set(p)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  const clearSelection = () => setSelectedIds(new Set())

  const toggleSortDirection = () => setSortDirection((p) => (p === 'asc' ? 'desc' : 'asc'))

  const toggleFilter = (key: FilterKey) => {
    setActiveFilters((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const removeGroupFilter = (key: FilterKey) => {
    setActiveFilters((prev) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }

  const activeFilterEntries = Array.from(activeFilters).map((key) => ({
    key,
    label: filterOptions.find((option) => option.value === key)?.label ?? key,
  }))
  const hasGroupFilters = search.trim().length > 0 || activeFilterEntries.length > 0

  const filtered = useMemo(() => {
    const result = (groups || []).filter((g) => {
      const matchSearch =
        g.group_name.toLowerCase().includes(search.toLowerCase()) ||
        g.church_org?.toLowerCase().includes(search.toLowerCase()) ||
        g.project_id?.toLowerCase().includes(search.toLowerCase())
      const matchFilters = Array.from(activeFilters).every((key) => {
        const val = g[key]
        return val === false || val === undefined || val === null
      })
      return matchSearch && matchFilters
    })
    result.sort((a, b) => {
      let c = 0
      switch (sortField) {
        case 'start_date':
          c = (a.start_date ?? '').localeCompare(b.start_date ?? '')
          break
        case 'group_name':
          c = a.group_name.localeCompare(b.group_name)
          break
        case 'status':
          c = { Confirmed: 0, Tentative: 1, Cancelled: 2 }[a.status] - { Confirmed: 0, Tentative: 1, Cancelled: 2 }[b.status]
          break
        case 'participants':
          c = a.participants - b.participants
          break
        case 'created_at':
          c = a.created_at.localeCompare(b.created_at)
          break
      }
      return sortDirection === 'asc' ? c : -c
    })
    return result
  }, [groups, search, activeFilters, sortField, sortDirection])

  const { upcoming, past } = useMemo(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd')
    const up: Group[] = []
    const pa: Group[] = []
    for (const g of filtered) {
      const end = (g.end_date ?? '').trim()
      const start = (g.start_date ?? '').trim()
      const inPast = end ? end < todayStr : start ? start < todayStr : false
      if (inPast) pa.push(g)
      else up.push(g)
    }
    return { upcoming: up, past: pa }
  }, [filtered])

  const allSelected = filtered.length > 0 && filtered.every((g) => selectedIds.has(g.id))
  const someSelected = filtered.some((g) => selectedIds.has(g.id))

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set())
    else setSelectedIds(new Set(filtered.map((g) => g.id)))
  }

  const handleBulkLeader = async (v: boolean) => {
    let n = 0
    for (const id of Array.from(selectedIds)) {
      try {
        await updateGroup.mutateAsync({ id, leader_assigned: v })
        n++
      } catch {
        // continue
      }
    }
    toast.success(`Updated ${n} group(s)`)
    clearSelection()
  }

  const handleBulkPretrip = async (v: boolean) => {
    let n = 0
    for (const id of Array.from(selectedIds)) {
      try {
        await updateGroup.mutateAsync({ id, pretrip_info_given: v })
        n++
      } catch {
        // continue
      }
    }
    toast.success(`Updated ${n} group(s)`)
    clearSelection()
  }

  const canEditGroup = (g: Group) => {
    // In simplified contact view, Volunteers are read-only on group actions.
    if (isGroupContactView && currentUserRole === UserRole.VOLUNTEER) return false
    return !isGroupContactView || (currentUserId != null && g.contactUserId != null && Number(g.contactUserId) === Number(currentUserId))
  }
  const effectiveRole = currentUserRole ?? session?.role
  const isJamaicaRegion = regionSlug?.toLowerCase() === 'jamaica'
  const showServiceReefSyncButton =
    isJamaicaRegion &&
    effectiveRole === UserRole.ADMIN &&
    !isGroupContactView

  const [serviceReefSyncing, setServiceReefSyncing] = useState(false)
  const runServiceReefSync = useCallback(async () => {
    setServiceReefSyncing(true)
    try {
      const res = await fetch('/api/servicereef/sync/full', {
        method: 'POST',
        credentials: 'include',
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const msg =
          typeof data?.message === 'string'
            ? data.message
            : typeof data?.error === 'string'
              ? data.error
              : `HTTP ${res.status}`
        throw new Error(msg)
      }
      const payload = data?.data ?? data
      if (payload && typeof payload === 'object') {
        const s = payload as {
          groupsCreated?: number
          usersCreated?: number
          membershipsUpserted?: number
          membershipsRemoved?: number
        }
        const description = [
          `New groups created: ${s.groupsCreated ?? 0}`,
          `New volunteers created: ${s.usersCreated ?? 0}`,
          `Group members updated: ${s.membershipsUpserted ?? 0}`,
          `Group members removed: ${s.membershipsRemoved ?? 0}`,
        ].join('\n')
        toast.success('Service Reef sync complete', { description })
      } else {
        toast.success('Service Reef sync completed')
      }
      await queryClient.invalidateQueries({ queryKey: ['stm', 'groups'] })
      await queryClient.invalidateQueries({ queryKey: ['stm', 'calendar'] })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Service Reef sync failed')
    } finally {
      setServiceReefSyncing(false)
    }
  }, [queryClient])

  const canSeeNoRevenueTint = effectiveRole === UserRole.ADMIN || effectiveRole === UserRole.BASE_LEADER
  const shouldUseNoRevenueTint = (g: Group) =>
    canSeeNoRevenueTint &&
    g.status !== 'Cancelled' &&
    categorizeProjectRevenue(g.project_id) === 'No Revenue'
  const isChecklistReadOnly = !!isGroupContactView

  useEffect(() => {
    if (!returnToGroupId || groupsLoading || hasRestoredScrollRef.current) return
    const target = document.getElementById(`group-card-${returnToGroupId}`)
    if (!target) return
    hasRestoredScrollRef.current = true
    target.scrollIntoView({ behavior: 'auto', block: 'center' })
    const params = new URLSearchParams(searchParams.toString())
    params.delete(SEARCH_PARAMS_RETURN_GROUP_ID_KEY)
    const nextUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname
    router.replace(nextUrl, { scroll: false })
  }, [returnToGroupId, groupsLoading, filtered.length, searchParams, pathname, router])

  return (
    <>
    <div className="space-y-4 sm:space-y-6 overflow-x-hidden min-w-0 w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Groups and Volunteers</h2>
        </div>
        <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-4 sm:justify-end sm:flex-1 sm:min-w-0">
        <div className="relative flex-1 min-w-[220px] sm:min-w-[360px] lg:min-w-[520px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground pointer-events-none z-10" />
          <Input
            placeholder="Search volunteers, groups, dates..."
            value={search}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value
              setSearch(value)
              updateSearchParam(value)
            }}
            className="pl-8 sm:pl-9 h-8 sm:h-10 text-sm"
          />
        </div>
        {search.trim().length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleOpenVolunteerSearch}
            className="h-8 sm:h-10 shrink-0 text-xs sm:text-sm whitespace-nowrap"
          >
            Search volunteers
          </Button>
        )}
        {!isGroupContactView && (
        <div className="flex items-center gap-2 shrink-0 sm:hidden">
          <Button
            onClick={() => setFormOpen(true)}
            size="sm"
            className="h-8 text-sm shrink-0"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Group
          </Button>
          {showServiceReefSyncButton && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-sm shrink-0"
              disabled={serviceReefSyncing}
              onClick={() => void runServiceReefSync()}
            >
              <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', serviceReefSyncing && 'animate-spin')} />
              Sync Service Reef
            </Button>
          )}
        </div>
        )}
        <div className="flex flex-nowrap items-center gap-1 sm:gap-4 shrink-0 w-full sm:w-auto min-w-0">
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <span className="text-[10px] sm:text-sm text-muted-foreground flex items-center gap-0.5 sm:gap-1 shrink-0">
            <Filter className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 shrink-0" /> Filter:
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-[72px] sm:w-[160px] h-7 sm:h-9 justify-between gap-0.5 sm:gap-1.5 text-[10px] sm:text-sm px-1.5 sm:px-3">
                <span className="truncate">
                  {activeFilters.size === 0
                    ? 'All'
                    : activeFilters.size === 1
                      ? filterOptions.find((o) => o.value === Array.from(activeFilters)[0])?.label ?? 'Filtered'
                      : `${activeFilters.size} selected`}
                </span>
                <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 shrink-0 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[200px]">
              {filterOptions.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={(e) => {
                    e.preventDefault()
                    toggleFilter(opt.value)
                  }}
                  className="relative flex items-center justify-between py-1.5 pl-2 pr-8 cursor-pointer"
                >
                  <span>Needs {opt.label}</span>
                  {activeFilters.has(opt.value) && (
                    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                      <Check className="h-4 w-4" />
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0 min-w-0">
            <span className="text-[10px] sm:text-sm text-muted-foreground flex items-center gap-0.5 sm:gap-1 shrink-0">
              <ArrowUpDown className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 shrink-0" /> Sort:
            </span>
          <Select value={sortField} onValueChange={(v: SortField) => setSortField(v)}>
            <SelectTrigger className="w-[78px] sm:w-[140px] h-7 sm:h-9 text-[10px] sm:text-sm px-1.5 sm:px-3 [&>svg]:h-3 [&>svg]:w-3 sm:[&>svg]:h-4 sm:[&>svg]:w-4">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={toggleSortDirection} className="px-1 sm:px-2 h-7 sm:h-9 w-7 sm:w-9 text-[10px] sm:text-sm">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </Button>
          </div>
        </div>
        {!isGroupContactView && (
        <div className="hidden sm:flex items-center gap-2 shrink-0 ml-auto sm:ml-0">
          <Button
            onClick={() => setFormOpen(true)}
            size="sm"
            className="h-8 sm:h-9 text-sm shrink-0"
          >
            <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-2" /> Add Group
          </Button>
          {showServiceReefSyncButton && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 sm:h-9 text-sm shrink-0"
              disabled={serviceReefSyncing}
              onClick={() => void runServiceReefSync()}
            >
              <RefreshCw className={cn('h-3.5 w-3.5 sm:h-4 sm:w-4 mr-2', serviceReefSyncing && 'animate-spin')} />
              Sync Service Reef
            </Button>
          )}
        </div>
        )}
      </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-muted-foreground">
          <span className="font-bold">
            Showing {filtered.length.toLocaleString()} group{filtered.length === 1 ? '' : 's'}
          </span>
        </div>
        {hasGroupFilters && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="w-4 h-4" />
              <span>Active filters:</span>
            </div>
            {search.trim().length > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
                <span className="font-medium">Search:</span>
                <span>{search.trim()}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 ml-1 hover:bg-primary/20"
                  onClick={clearSearchParam}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
            {activeFilterEntries.map(({ key, label }) => (
              <div
                key={key}
                className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm"
              >
                <span className="font-medium">Filter:</span>
                <span>{label}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 ml-1 hover:bg-primary/20"
                  onClick={() => removeGroupFilter(key)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearGroupFilters}
              className="text-xs"
            >
              Clear all
            </Button>
          </div>
        )}
      </div>

      {!isGroupContactView && someSelected && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-primary/10 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="h-4 w-px bg-border" />
          <Button size="sm" variant="outline" onClick={() => handleBulkLeader(true)} className="gap-1.5">
            <UserCheck className="h-3.5 w-3.5" /> Mark Leader Assigned
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBulkLeader(false)} className="gap-1.5">
            <UserCheck className="h-3.5 w-3.5" /> Unmark Leader
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBulkPretrip(true)} className="gap-1.5">
            <FileCheck className="h-3.5 w-3.5" /> Mark Pre-Trip Given
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBulkPretrip(false)} className="gap-1.5">
            <FileCheck className="h-3.5 w-3.5" /> Unmark Pre-Trip
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSelection} className="ml-auto">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="grid gap-3 min-w-0">
        {groupsLoading ? (
          <p className="text-muted-foreground py-8 text-center">Loading Groups</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">No groups found</p>
        ) : (
          <>
            {upcoming.map((g) => (
            <div
              key={g.id}
              id={`group-card-${g.id}`}
              className={cn(
                'flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors min-w-0',
                shouldUseNoRevenueTint(g) && 'bg-slate-100/65 border-slate-300 dark:bg-slate-900/40 dark:border-slate-700',
                g.status === 'Confirmed' && 'border-l-4 border-l-green-500',
                g.status === 'Tentative' && 'border-l-4 border-l-amber-500 border-dashed',
                g.status === 'Cancelled' && 'opacity-60',
                selectedIds.has(g.id) && 'ring-2 ring-primary/50 bg-primary/5'
              )}
            >
              <div className="flex flex-1 min-w-0 flex-col gap-1">
              <div className="flex justify-between items-start gap-2 w-full">
                <div className="min-w-0 flex-1 flex flex-col gap-1">
                  <Link
                    href={groupDetailHref(g.id)}
                    className="block rounded-md p-0.5 -m-0.5 text-left outline-offset-2 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-primary">{g.group_name}</span>
                      {g.project_id && (
                        <span className="text-xs text-muted-foreground font-mono flex items-center gap-0.5">
                          <Hash className="h-3 w-3" />
                          {g.project_id}
                        </span>
                      )}
                      <Badge
                        variant={
                          g.status === 'Confirmed' ? 'default' : g.status === 'Cancelled' ? 'destructive' : 'secondary'
                        }
                      >
                        {g.status}
                      </Badge>
                      {(g.retention_group === 'New' || g.retention_group === 'Return' || g.retention_group === 'Retention') && (
                        <Badge
                          variant="outline"
                          className="border border-gray-500 bg-gray-500/15 text-gray-700 dark:bg-gray-700/30 dark:text-gray-300"
                        >
                          {g.retention_group === 'Retention' ? 'Return' : g.retention_group}
                        </Badge>
                      )}
                      {g.trip_type ? (
                        <Badge variant="outline" className={cn('border', tripTypeStyles[g.trip_type])}>
                          {g.trip_type}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground">
                          Trip type not set
                        </Badge>
                      )}
                      {g.has_recent_member_join && (
                        <Badge
                          variant="outline"
                          className="border-amber-500/80 bg-amber-500/15 text-amber-900 dark:text-amber-100"
                        >
                          New Members
                        </Badge>
                      )}
                    </div>
                  </Link>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <Link
                      href={groupDetailHref(g.id)}
                      className="inline-flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground hover:text-foreground"
                    >
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {g.participants}
                      </span>
                      <span>{formatGroupDateRange(g.start_date, g.end_date)}</span>
                      {g.contact_name && (
                        <span className="min-w-0 max-w-full truncate">
                          <span className="sm:hidden">GL: </span>
                          <span className="hidden sm:inline">GroupLeader: </span>
                          {g.contact_name}
                        </span>
                      )}
                    </Link>
                    {g.contact_phone && (
                      <a
                        href={`tel:${g.contact_phone}`}
                        className="inline-flex shrink-0 items-center gap-1 text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="h-3 w-3" />
                        {g.contact_phone}
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 sm:ml-4 ml-auto">
                  {!isGroupContactView && (
                  <Checkbox
                    checked={selectedIds.has(g.id)}
                    onCheckedChange={() => toggleSelection(g.id)}
                    className="shrink-0"
                  />
                  )}
                  {canEditGroup(g) && (
                    <Button variant="outline" size="icon" className="sm:h-9 sm:w-auto sm:min-w-0 sm:px-3 sm:gap-2" asChild>
                      <Link
                        href={groupVolunteersHref(g.id)}
                        title="Volunteers"
                        aria-label="Volunteers"
                      >
                        <Users className="h-4 w-4 shrink-0" />
                        <span className="hidden sm:inline">Add volunteers to group</span>
                      </Link>
                    </Button>
                  )}
                  {canEditGroup(g) && (
                    <Button variant="outline" size="icon" className="sm:h-9 sm:w-auto sm:min-w-0 sm:px-3 sm:gap-2" asChild>
                      <Link href={groupDetailHref(g.id)} title="Edit group" aria-label="Edit group">
                        <Edit2 className="h-4 w-4 shrink-0" />
                        <span className="hidden sm:inline">Edit Group</span>
                      </Link>
                    </Button>
                  )}
                  {!isGroupContactView && (
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteGroup.mutate(g.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  )}
                </div>
              </div>
                <div className="mt-2 pt-2 border-t border-border/50 space-y-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.leader_assigned}
                      onCheckedChange={() => handleToggleLeader(g)}
                      disabled={isChecklistReadOnly}
                      id={`leader-${g.id}`}
                    />
                    <label htmlFor={`leader-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <UserCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Leader Assigned
                    </label>
                    {g.leader_name && (
                      <span className="text-xs sm:text-sm text-muted-foreground ml-1">({g.leader_name})</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.pretrip_info_given}
                      onCheckedChange={() => handleTogglePretrip(g)}
                      disabled={isChecklistReadOnly}
                      id={`pretrip-${g.id}`}
                    />
                    <label htmlFor={`pretrip-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <FileCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Pre-Trip Info
                    </label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.deposit_received ?? false}
                      onCheckedChange={() => handleToggleDeposit(g)}
                      disabled={isChecklistReadOnly}
                      id={`deposit-${g.id}`}
                    />
                    <label htmlFor={`deposit-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <Banknote className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Deposit Received
                    </label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.background_check_complete ?? false}
                      onCheckedChange={() => handleToggleBackgroundCheck(g)}
                      disabled={isChecklistReadOnly}
                      id={`bgcheck-${g.id}`}
                    />
                    <label htmlFor={`bgcheck-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <ShieldCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Background Check
                    </label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.full_payment_received ?? false}
                      onCheckedChange={() => handleToggleFullPayment(g)}
                      disabled={isChecklistReadOnly}
                      id={`fullpay-${g.id}`}
                    />
                    <label htmlFor={`fullpay-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <CreditCard className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Full Payment
                    </label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.post_trip_info ?? false}
                      onCheckedChange={() => handleTogglePostTrip(g)}
                      disabled={isChecklistReadOnly}
                      id={`posttrip-${g.id}`}
                    />
                    <label htmlFor={`posttrip-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <FileText className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Post Trip Info
                    </label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={isDietaryRestrictionsChecked(g)}
                      onCheckedChange={() => handleToggleDietaryRestrictions(g)}
                      disabled={isChecklistReadOnly}
                      id={`dietary-${g.id}`}
                    />
                    <label htmlFor={`dietary-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <UtensilsCrossed className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Dietary Restrictions
                    </label>
                  </div>
                  </div>
                </div>
              </div>
            </div>
            ))}
            {past.length > 0 && (
              <div className="space-y-3 mt-6">
                <h3 className="text-sm font-semibold text-muted-foreground tracking-tight">Past Groups</h3>
                {past.map((g) => (
            <div
              key={g.id}
              id={`group-card-${g.id}`}
              className={cn(
                'flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors min-w-0',
                shouldUseNoRevenueTint(g) && 'bg-slate-100/65 border-slate-300 dark:bg-slate-900/40 dark:border-slate-700',
                g.status === 'Confirmed' && 'border-l-4 border-l-green-500',
                g.status === 'Tentative' && 'border-l-4 border-l-amber-500 border-dashed',
                g.status === 'Cancelled' && 'opacity-60',
                selectedIds.has(g.id) && 'ring-2 ring-primary/50 bg-primary/5'
              )}
            >
              <div className="flex flex-1 min-w-0 flex-col gap-1">
              <div className="flex justify-between items-start gap-2 w-full">
                <div className="min-w-0 flex-1 flex flex-col gap-1">
                  <Link
                    href={groupDetailHref(g.id)}
                    className="block rounded-md p-0.5 -m-0.5 text-left outline-offset-2 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-primary">{g.group_name}</span>
                      {g.project_id && (
                        <span className="text-xs text-muted-foreground font-mono flex items-center gap-0.5">
                          <Hash className="h-3 w-3" />
                          {g.project_id}
                        </span>
                      )}
                      <Badge
                        variant={
                          g.status === 'Confirmed' ? 'default' : g.status === 'Cancelled' ? 'destructive' : 'secondary'
                        }
                      >
                        {g.status}
                      </Badge>
                      {(g.retention_group === 'New' || g.retention_group === 'Return' || g.retention_group === 'Retention') && (
                        <Badge
                          variant="outline"
                          className="border border-gray-500 bg-gray-500/15 text-gray-700 dark:bg-gray-700/30 dark:text-gray-300"
                        >
                          {g.retention_group === 'Retention' ? 'Return' : g.retention_group}
                        </Badge>
                      )}
                      {g.trip_type ? (
                        <Badge variant="outline" className={cn('border', tripTypeStyles[g.trip_type])}>
                          {g.trip_type}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground">
                          Trip type not set
                        </Badge>
                      )}
                      {g.has_recent_member_join && (
                        <Badge
                          variant="outline"
                          className="border-amber-500/80 bg-amber-500/15 text-amber-900 dark:text-amber-100"
                        >
                          New Members
                        </Badge>
                      )}
                    </div>
                  </Link>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <Link
                      href={groupDetailHref(g.id)}
                      className="inline-flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground hover:text-foreground"
                    >
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {g.participants}
                      </span>
                      <span>{formatGroupDateRange(g.start_date, g.end_date)}</span>
                      {g.contact_name && (
                        <span className="min-w-0 max-w-full truncate">
                          <span className="sm:hidden">GL: </span>
                          <span className="hidden sm:inline">GroupLeader: </span>
                          {g.contact_name}
                        </span>
                      )}
                    </Link>
                    {g.contact_phone && (
                      <a
                        href={`tel:${g.contact_phone}`}
                        className="inline-flex shrink-0 items-center gap-1 text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="h-3 w-3" />
                        {g.contact_phone}
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 sm:ml-4 ml-auto">
                  {!isGroupContactView && (
                  <Checkbox
                    checked={selectedIds.has(g.id)}
                    onCheckedChange={() => toggleSelection(g.id)}
                    className="shrink-0"
                  />
                  )}
                  {canEditGroup(g) && (
                    <Button variant="outline" size="icon" className="sm:h-9 sm:w-auto sm:min-w-0 sm:px-3 sm:gap-2" asChild>
                      <Link
                        href={groupVolunteersHref(g.id)}
                        title="Volunteers"
                        aria-label="Volunteers"
                      >
                        <Users className="h-4 w-4 shrink-0" />
                        <span className="hidden sm:inline">Add volunteers to group</span>
                      </Link>
                    </Button>
                  )}
                  {canEditGroup(g) && (
                    <Button variant="outline" size="icon" className="sm:h-9 sm:w-auto sm:min-w-0 sm:px-3 sm:gap-2" asChild>
                      <Link href={groupDetailHref(g.id)} title="Edit group" aria-label="Edit group">
                        <Edit2 className="h-4 w-4 shrink-0" />
                        <span className="hidden sm:inline">Edit Group</span>
                      </Link>
                    </Button>
                  )}
                  {!isGroupContactView && (
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteGroup.mutate(g.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  )}
                </div>
              </div>
                <div className="mt-2 pt-2 border-t border-border/50 space-y-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.leader_assigned}
                      onCheckedChange={() => handleToggleLeader(g)}
                      disabled={isChecklistReadOnly}
                      id={`leader-${g.id}`}
                    />
                    <label htmlFor={`leader-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <UserCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Leader Assigned
                    </label>
                    {g.leader_name && (
                      <span className="text-xs sm:text-sm text-muted-foreground ml-1">({g.leader_name})</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.pretrip_info_given}
                      onCheckedChange={() => handleTogglePretrip(g)}
                      disabled={isChecklistReadOnly}
                      id={`pretrip-${g.id}`}
                    />
                    <label htmlFor={`pretrip-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <FileCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Pre-Trip Info
                    </label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.deposit_received ?? false}
                      onCheckedChange={() => handleToggleDeposit(g)}
                      disabled={isChecklistReadOnly}
                      id={`deposit-${g.id}`}
                    />
                    <label htmlFor={`deposit-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <Banknote className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Deposit Received
                    </label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.background_check_complete ?? false}
                      onCheckedChange={() => handleToggleBackgroundCheck(g)}
                      disabled={isChecklistReadOnly}
                      id={`bgcheck-${g.id}`}
                    />
                    <label htmlFor={`bgcheck-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <ShieldCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Background Check
                    </label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.full_payment_received ?? false}
                      onCheckedChange={() => handleToggleFullPayment(g)}
                      disabled={isChecklistReadOnly}
                      id={`fullpay-${g.id}`}
                    />
                    <label htmlFor={`fullpay-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <CreditCard className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Full Payment
                    </label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={g.post_trip_info ?? false}
                      onCheckedChange={() => handleTogglePostTrip(g)}
                      disabled={isChecklistReadOnly}
                      id={`posttrip-${g.id}`}
                    />
                    <label htmlFor={`posttrip-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <FileText className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Post Trip Info
                    </label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Checkbox
                      checked={isDietaryRestrictionsChecked(g)}
                      onCheckedChange={() => handleToggleDietaryRestrictions(g)}
                      disabled={isChecklistReadOnly}
                      id={`dietary-${g.id}`}
                    />
                    <label htmlFor={`dietary-${g.id}`} className="text-xs sm:text-sm flex items-center gap-1 cursor-pointer">
                      <UtensilsCrossed className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                      Dietary Restrictions
                    </label>
                  </div>
                  </div>
                </div>
              </div>
            </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <GroupFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        regionId={regionId}
        checklistReadOnly={!!isGroupContactView}
      />
    </div>
    </>
  )
}
